import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Chip,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

// Server rejects entity.* override forms longer than 50 chars while the
// legacy caption columns still exist.
const LEGACY_MAX_LENGTH = 50;

const TermEditor = ({
	term,
	readOnly,
	saving,
	onSave,
	onSaveAndNext,
	onReset,
	lastEditedAt,
}) => {
	const { t } = useTranslation();
	const [singular, setSingular] = useState("");
	const [plural, setPlural] = useState("");

	useEffect(() => {
		setSingular(term?.effectiveSingular ?? "");
		setPlural(term?.effectivePlural ?? "");
	}, [
		term?.key,
		term?.effectiveSingular,
		term?.effectivePlural,
	]);

	if (!term) {
		return (
			<Box sx={{ p: 4, textAlign: "center" }}>
				<Typography variant="body1" color="text.secondary">
					{t("message.selectTermToEdit")}
				</Typography>
			</Box>
		);
	}

	const dirty =
		singular !== term.effectiveSingular ||
		(term.hasPlural && plural !== term.effectivePlural);
	const complete =
		singular.trim() !== "" && (!term.hasPlural || plural.trim() !== "");
	const canSave = !readOnly && !saving && dirty && complete;
	const maxLength = term.key.startsWith("entity.")
		? LEGACY_MAX_LENGTH
		: undefined;

	const values = { singular: singular.trim(), plural: plural.trim() };

	const handleKeyDown = (e) => {
		if (e.key !== "Enter") return;
		e.preventDefault();

		if (!canSave) return;

		if (e.metaKey || e.ctrlKey) {
			onSaveAndNext(values);
		} else {
			onSave(values);
		}
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography variant="h6">{term.label}</Typography>
				<Chip label={term.key} size="small" variant="outlined" />
				<Chip
					label={
						term.isOverridden
							? t("label.overridden")
							: t("label.defaultStatus")
					}
					size="small"
					color={term.isOverridden ? "primary" : "default"}
				/>
			</Box>

			<Typography variant="body2" color="text.secondary">
				{t("label.defaultColon")}{" "}
				{term.hasPlural
					? `${term.defaultSingular} / ${term.defaultPlural}`
					: term.defaultSingular}
			</Typography>

			<TextField
				label={term.hasPlural ? t("label.singular") : t("label.value")}
				value={singular}
				onChange={(e) => setSingular(e.target.value)}
				onKeyDown={handleKeyDown}
				disabled={readOnly || saving}
				error={dirty && singular.trim() === ""}
				helperText={
					dirty && singular.trim() === ""
						? t("message.valueCannotBeEmptyReset")
						: t("label.defaultValue", { value: term.defaultSingular })
				}
				inputProps={{ maxLength }}
				fullWidth
			/>

			{term.hasPlural && (
				<TextField
					label={t("label.plural")}
					value={plural}
					onChange={(e) => setPlural(e.target.value)}
					onKeyDown={handleKeyDown}
					disabled={readOnly || saving}
					error={dirty && plural.trim() === ""}
					helperText={
						dirty && plural.trim() === ""
							? t("message.valueCannotBeEmptyReset")
							: t("label.defaultValue", { value: term.defaultPlural })
					}
					inputProps={{ maxLength }}
					fullWidth
				/>
			)}

			<Box sx={{ display: "flex", gap: 1 }}>
				<Button
					variant="contained"
					disabled={!canSave}
					onClick={() => onSave(values)}
				>
					{t("action.save")}
				</Button>
				<Tooltip title={t("label.ctrlCmdEnter")}>
					<span>
						<Button
							variant="outlined"
							disabled={!canSave}
							onClick={() => onSaveAndNext(values)}
						>
							{t("action.saveAndEditNext")}
						</Button>
					</span>
				</Tooltip>
				<Box sx={{ flexGrow: 1 }} />
				<Button
					color="error"
					disabled={readOnly || saving || !term.isOverridden}
					onClick={() => onReset()}
				>
					{t("action.resetToDefault")}
				</Button>
			</Box>

			<Typography variant="caption" color="text.secondary">
				{lastEditedAt
					? t("message.lastEditedAtSession", {
							time: new Date(lastEditedAt).toLocaleTimeString(),
					  })
					: term.isOverridden
					? t("message.overriddenForSiteApp")
					: t("message.usingPlatformDefault")}
			</Typography>
		</Box>
	);
};

export default TermEditor;
